/**
 * ZAPPY BOI — Arc Projector (doc 05). 1x1, 40 alloy, 160 HP, 8 dmg chains to 3,
 * 0.8/s, range 5, 2 capacitor/zap.
 *
 * "Static electricity, but as a lifestyle."
 *
 * Silhouette family: COIL-ORB. A slim column through two wide insulator rings,
 * crowned by an orb — nothing else in the tower class carries a sphere, so the
 * black-shape test reads it instantly. Omnidirectional by nature: the draw
 * ignores facing, which is honest (an arc has no barrel).
 *
 * Signature: warm red tower lamp on the collar (doc 10 families). The ARC
 * itself renders cold-white at the effect layer — effects are not signatures.
 */

import { tiersOf, type BuildContext, type EntityDraw } from '../entity.ts';
import { rampFor, sigRamp, signatureColor, SIGNATURE_TOWER } from '../materials.ts';
import { isoCylinder, isoDiamondRim, isoPrism, type EntityCanvas } from '../../render/primitives.ts';

/**
 * Upgrade motifs (doc 05's 3×3):
 *   A · MORE TENTACLES  emitter nodes, ending in a halo ring over the orb
 *   B · STATIC CLING    capacitor cans, ending in a rod cage
 *   C · CRISPY          the orb runs hot, ending in a burning outer shell
 */
export const drawZappy: EntityDraw = (c: EntityCanvas, ctx: BuildContext): void => {
  const r = rampFor(ctx.depth);
  const sig = sigRamp(SIGNATURE_TOWER);
  const lamp = signatureColor(sig, ctx.state);
  const up = tiersOf(ctx);

  // plinth
  isoPrism(c, { n: 1, inset: 1, height: 2, ramp: r });
  isoDiamondRim(c.s, c.ax, c.ay - 2, 7, r.edge);

  // the coil is a BEEHIVE of discs, not a mast: wide insulator rings tapering
  // into the orb. Nothing else in the class is round-shouldered like this —
  // the first draft shared 82% of Stabby's silhouette and the harness said no.
  isoCylinder(c, { rx: 10, height: 3, lift: 2, ramp: r });
  isoCylinder(c, { rx: 4, height: 2, lift: 5, ramp: r });
  isoCylinder(c, { rx: 7, height: 3, lift: 7, ramp: r });
  isoCylinder(c, { rx: 3, height: 1, lift: 10, ramp: r });

  // the orb — the whole tower is squat and round-shouldered on purpose
  const orbTop = isoCylinder(c, { rx: 5, height: 5, lift: 11, ramp: r });
  c.s.px(c.ax - 2, orbTop + 2, r.edge); // specular glint, upper-left light

  // selected: the wide skirt hides most of the pad rim, so the tower itself
  // answers — a bright inspection ring on the big disc
  if (ctx.state === 'selected') {
    isoDiamondRim(c.s, c.ax, c.ay - 5, 4, r.edge);
  }

  // collar lamp — the tower-red signature
  if (lamp !== null) {
    c.s.fillRect(c.ax - 1, c.ay - 6, 3, 2, lamp);
  }

  // firing: the orb goes hot and sheds two static sparks (phase-stepped so the
  // loop closes; Law L4 keeps every spark on the grid)
  if (ctx.state === 'firing') {
    const k = Math.round(ctx.phase * 2) % 2;
    c.s.fillRect(c.ax - 2, orbTop + 1, 4, 3, sig.hot, 'set');
    c.s.px(c.ax - 4 + k, orbTop, sig.halo);
    c.s.px(c.ax + 3 - k, orbTop + 3, sig.halo);
  } else if (ctx.state === 'tracking' || ctx.state === 'working') {
    c.s.fillRect(c.ax - 1, orbTop + 2, 2, 1, sig.on);
  }

  // --- A · MORE TENTACLES: emitter nodes, then a halo ring -------------------
  if (up.a >= 1) {
    for (const side of [-1, 1]) {
      c.s.fillRect(c.ax + side * 8 - 1, c.ay - 6, 2, 3, r.left);
      c.s.px(c.ax + side * 8, c.ay - 7, r.edge);
    }
  }
  if (up.a >= 2) {
    for (const dy of [-2, 2]) {
      c.s.fillRect(c.ax - 1, c.ay - 6 + dy, 2, 3, r.left);
      c.s.px(c.ax, c.ay - 7 + dy, r.edge);
    }
  }
  if (up.a >= 3) {
    // "Group Chat": six nodes on a halo above the orb — a new silhouette
    const hy = orbTop - 1;
    for (let k = 0; k < 6; k++) {
      const dx = [8, 5, -5, -8, -5, 5][k]!;
      const dy = [0, 3, 3, 0, -3, -3][k]!;
      c.s.fillRect(c.ax + dx - 1, hy + dy - 1, 2, 2, r.edge);
    }
    isoDiamondRim(c.s, c.ax, hy + 1, 4, r.left);
  }

  // --- B · STATIC CLING: capacitor cans, then the rod cage -------------------
  if (up.b >= 1) isoCylinder(c, { rx: 2, height: 5, lift: 3, dx: -8, dy: 3, ramp: r });
  if (up.b >= 2) {
    isoCylinder(c, { rx: 2, height: 5, lift: 3, dx: 8, dy: 3, ramp: r });
    c.s.fillRect(c.ax - 4, c.ay - 9, 8, 1, r.edge);
  }
  if (up.b >= 3) {
    // "Time-Out Corner": three rods stand past the orb and spark at the tips
    for (const dx of [-6, 0, 6]) {
      c.s.fillRect(c.ax + dx - 1, orbTop - 6, 2, 9, r.left);
      c.s.px(c.ax + dx, orbTop - 7, ctx.state === 'firing' ? sig.hot : r.edge);
    }
  }

  // --- C · CRISPY: the orb runs hot ------------------------------------------
  if (up.c >= 1) c.s.fillRect(c.ax - 5, c.ay - 8, 11, 1, sig.on);
  if (up.c >= 2) {
    c.s.fillRect(c.ax - 2, orbTop + 3, 5, 2, sig.on, 'set');
    for (const side of [-1, 1]) c.s.fillRect(c.ax + side * 6 - 1, c.ay - 12, 2, 4, r.edge);
  }
  if (up.c >= 3) {
    // "Overcooked": a burning shell around the orb, and it smokes
    isoDiamondRim(c.s, c.ax, orbTop + 3, 7, sig.hot);
    c.s.fillRect(c.ax - 3, orbTop + 1, 7, 3, sig.hot, 'set');
    c.s.px(c.ax - 1, orbTop - 3, sig.halo);
    c.s.px(c.ax + 2, orbTop - 5, sig.halo);
  }

  // dry = capacitor empty: a dark drained band across the big ring
  if (ctx.state === 'dry') {
    c.s.fillRect(c.ax - 6, c.ay - 4, 12, 1, r.deep);
    c.s.fillRect(c.ax - 2, orbTop + 2, 4, 2, r.deep, 'set');
  }
};
